import { Sparkles, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

import { MAX_ALT_TEXT_LENGTH, isVideoContentType } from "./story-form-utils"

interface Props {
  id: string
  preview: string | File
  contentType?: string
  altText: string
  onAltTextChange: (value: string) => void
  onRemove: () => void
  onRegenerateAltText?: () => void
  isGeneratingAltText?: boolean
}

export default function MediaFileRow({
  id,
  preview,
  contentType,
  altText,
  onAltTextChange,
  onRemove,
  onRegenerateAltText,
  isGeneratingAltText,
}: Props) {
  const src = preview instanceof File ? URL.createObjectURL(preview) : preview
  const resolvedContentType =
    contentType ?? (preview instanceof File ? preview.type : undefined)
  const canRegenerate =
    !!onRegenerateAltText && !isVideoContentType(resolvedContentType)

  return (
    <div className="grid gap-3 rounded-lg border p-3 md:grid-cols-[6rem_minmax(0,1fr)]">
      <div className="relative w-fit">
        {isVideoContentType(resolvedContentType) ? (
          <Dialog>
            <DialogTrigger asChild>
              <video
                src={src}
                className="h-24 w-24 cursor-pointer rounded-md object-cover"
                muted
                playsInline
                preload="metadata"
              />
            </DialogTrigger>
            <DialogContent className="p-2 sm:max-w-4xl">
              <DialogTitle className="sr-only">Video preview</DialogTitle>
              <video
                src={src}
                className="max-h-[80vh] w-full rounded-md object-contain"
                controls
                autoPlay
                playsInline
                preload="metadata"
              />
              {altText && (
                <p className="text-muted-foreground px-2 pb-2 text-sm">
                  {altText}
                </p>
              )}
            </DialogContent>
          </Dialog>
        ) : (
          <Dialog>
            <DialogTrigger asChild>
              <img
                src={src}
                alt={altText}
                className="h-24 w-24 cursor-pointer rounded-md object-cover"
              />
            </DialogTrigger>
            <DialogContent className="p-2 sm:max-w-4xl">
              <DialogTitle className="sr-only">Image preview</DialogTitle>
              <img
                src={src}
                alt={altText}
                className="max-h-[80vh] w-full rounded-md object-contain"
              />
              {altText && (
                <p className="text-muted-foreground px-2 pb-2 text-sm">
                  {altText}
                </p>
              )}
            </DialogContent>
          </Dialog>
        )}
        <button
          type="button"
          onClick={onRemove}
          className="bg-background/80 absolute -top-1.5 -right-1.5 rounded-full p-0.5"
        >
          <X className="size-3" />
        </button>
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={id}>Alt text</Label>
        <div className="relative">
          <Textarea
            id={id}
            value={altText}
            onChange={(e) => onAltTextChange(e.target.value)}
            maxLength={MAX_ALT_TEXT_LENGTH}
            rows={2}
            placeholder={
              isGeneratingAltText
                ? "Generating alt text..."
                : "Describe the media file for screen readers"
            }
            className={`resize-none ${isGeneratingAltText ? "animate-pulse" : ""}`}
          />
        </div>
        {canRegenerate && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={onRegenerateAltText}
            disabled={isGeneratingAltText}
          >
            <Sparkles className="size-4" />
            Update alt text
          </Button>
        )}
      </div>
    </div>
  )
}
